/** @module @liquidefy/code-editor/keybindings/chords @version 3.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  KeybindingsState,
  KeyBinding,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
} from '../code-editor.js';

import { asChordId } from '../code-editor-types.js';

import { normalizeKey } from './keybindings.js';

// ═══ §2 CHORD LIFECYCLE ═══

/**
 * Records the first key of a chord sequence. Clears any previous pending chord.
 */
export function startChord(
  state: KeybindingsState,
  firstKey: string,
): KeybindingsState {
  const normalized = normalizeKey(firstKey);
  return {
    ...state,
    pendingChordKey: normalized,
    pendingChordId: asChordId(generateId('chord')),
  };
}

/**
 * Completes a chord with the second key. Finds a matching chord binding.
 * Returns the updated state with the resolved binding's command available via the result value,
 * and clears the pending chord. Fails if no binding matches.
 */
export function resolveChord(
  state: KeybindingsState,
  secondKey: string,
): EditorResult<{ state: KeybindingsState; binding: KeyBinding }> {
  if (state.pendingChordKey === undefined) {
    return err('NO_PENDING_CHORD', 'No chord sequence is pending');
  }

  const chordKey = buildChordKey(state.pendingChordKey, normalizeKey(secondKey));

  const match = state.bindings.find((b) => {
    if (!b.isChord) return false;
    const bindingChord = buildChordKey(
      normalizeKey(b.chordFirstPart ?? ''),
      normalizeKey(b.chordSecondPart ?? ''),
    );
    return bindingChord === chordKey;
  });

  if (!match) {
    return err('NO_CHORD_BINDING', `No binding found for chord '${chordKey}'`);
  }

  const cleared = cancelChord(state);
  return ok(
    { state: cleared, binding: match },
    'CHORD_RESOLVED',
    `Chord '${chordKey}' resolved to command '${match.command}'`,
  );
}

/**
 * Clears any pending chord sequence.
 */
export function cancelChord(state: KeybindingsState): KeybindingsState {
  return {
    ...state,
    pendingChordKey: undefined,
    pendingChordId: undefined,
  };
}

// ═══ §3 CHORD QUERIES ═══

/**
 * Returns true if a chord sequence is currently awaiting its second key.
 */
export function hasPendingChord(state: KeybindingsState): boolean {
  return state.pendingChordKey !== undefined;
}

/**
 * Returns the first key of the pending chord, or undefined.
 */
export function getPendingChordKey(state: KeybindingsState): string | undefined {
  return state.pendingChordKey;
}

// ═══ §4 CHORD CONSTRUCTION & PARSING ═══

/**
 * Combines two normalized key parts into a chord string.
 * e.g. buildChordKey('ctrl+k', 'ctrl+c') → 'ctrl+k ctrl+c'
 */
export function buildChordKey(first: string, second: string): string {
  return `${normalizeKey(first)} ${normalizeKey(second)}`;
}

/**
 * Splits a chord string back into its two constituent parts.
 * Returns undefined if the input is not a valid two-part chord.
 */
export function parseChord(chord: string): [string, string] | undefined {
  const trimmed = chord.trim();
  // A chord has exactly one space separating two key expressions
  const spaceIdx = trimmed.indexOf(' ');
  if (spaceIdx === -1) return undefined;

  const first = trimmed.slice(0, spaceIdx).trim();
  const second = trimmed.slice(spaceIdx + 1).trim();

  if (first.length === 0 || second.length === 0) return undefined;
  // second must have no further spaces (we only support two-part chords)
  if (second.includes(' ')) return undefined;

  return [first, second];
}
